<template>
    <div>
        <div class="mb-4">
            <h4 class="ctc-dashboard-title mb-1">Employee Cost Settings</h4>
            <div class="ctc-dashboard-subtitle">Configure CTC heads, their rules and employee eligibility</div>
        </div>
        <div class="d-flex mb-4 align-items-center">
            <div class="input-group search-input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text">
                        <b-icon icon="search" class="search-icon"></b-icon>
                    </span>
                </div>
                <input type="text" class="form-control"
                    placeholder="Search"
                    v-model="search"
                    @input="resetPagination()">
            </div>

            <!-- Record counter beside the search box, as on the employee list:
                 it tracks the filtered set, so it answers "how many match" while
                 a search is typed rather than only the grand total. -->
            <div class="ctc-count-badge ml-2">
                <span class="ctc-count-icon"><b-icon icon="list-ul"></b-icon></span>
                <span class="ctc-count-number">{{ filteredData.length }}</span>
            </div>

            <div class="ml-auto d-flex align-items-center" v-if="$acl && $acl['cost-to-company'] == 2">
                <!-- All three wear the same treatment as "Generate Projection" on
                     the Annual CTC Reports panel: the plain system button, styled
                     globally in resources/sass/atlhr.scss. -->
                <b-button class="ctc-header-btn" @click="addNew()"><b-icon icon="plus" class="mr-1"></b-icon>Add New Cost</b-button>
                <b-button class="ctc-header-btn ml-2" v-if="configMode == 2" @click="openTemplate(1)">Compensation Statement Template</b-button>
                <b-button class="ctc-header-btn ml-2" v-if="configMode == 2" @click="openTemplate(2)">Annual Year Statement Template</b-button>
            </div>
        </div>

        <div class="table-container-custom table-responsive mb-4">
            <table class="table mb-0 ctc-config-table">
                <thead>
                    <tr class="table-head">
                        <template v-for="column in columns">
                            <template v-if="(column.name !== 'category' && column.name !== 'fixed_amount' && column.name !== 'departments') || configMode != 2">
                                <th v-if="column.sort" @click="sortBy(column.name)"
                                    :class="[sortKey === column.name ? (sortOrders[column.name] > 0 ? 'sorting_asc' : 'sorting_desc') : 'sorting', column.name === 'fixed_amount' ? 'text-right' : '']"
                                    style="cursor:pointer;">
                                    <span>{{ column.label }}</span>
                                </th>
                                <th v-else :class="column.name === 'fixed_amount' ? 'text-right' : ''">
                                    <span>{{ column.label }}</span>
                                </th>
                            </template>
                        </template>
                        <th class="text-right" v-if="$acl && $acl['cost-to-company'] == 2">Actions</th>
                    </tr>
                </thead>

                <tbody>
                    <tr class="table-row" v-for="item in paginatedData" :key="item.id">
                        <td class="align-middle font-weight-500 text-dark">{{ item.name }}</td>
                        <td class="align-middle">{{ getTypeText(item.type) }}</td>
                        <td class="align-middle" v-if="configMode != 2">{{ getCategoryText(item.category) }}</td>
                        <td class="align-middle">{{ getFrequencyText(item.payment_frequency) }}</td>
                        <!-- A head driven by conditional rules has no single fixed
                             amount to show: the rule tree supplies the figure per
                             employee, so the column reads "-" as it does for GF. -->
                        <td class="align-middle text-right" v-if="configMode != 2">{{ (item.is_gf == 1 || item.has_rule_config == 1) ? '-' : '৳ ' + formatAmount(item.fixed_amount) }}</td>
                        <td class="align-middle" v-if="configMode != 2">
                            <span class="dept-badge mr-1 mb-1" v-for="dept in (item.departments || []).slice(0, 2)" :key="dept.id">
                                {{ dept.id == -1 ? 'All Department' : dept.name }}
                            </span>
                            <span class="dept-badge font-weight-bold" v-if="(item.departments || []).length > 2">
                                +{{ item.departments.length - 2 }}
                            </span>
                        </td>
                        <td class="align-middle">
                            <!-- The gap between icon and label comes from the
                                 pill's flex `gap`, so no margin utility here. -->
                            <span class="status-badge status-active" v-if="item.status === 'active'">
                                <b-icon icon="check-circle-fill"></b-icon><span>Active</span>
                            </span>
                            <span class="status-badge status-disabled" v-else>
                                <b-icon icon="dash-circle-fill"></b-icon><span>Inactive</span>
                            </span>
                        </td>
                        <td class="align-middle text-right position-relative" v-if="$acl && $acl['cost-to-company'] == 2">
                            <a id="actionDropdown" class="dropdown-toggle clearfix action-dropdown-toggle"
                               style="font-size: 16px;" href="#" role="button" data-toggle="dropdown" aria-haspopup="true"
                               aria-expanded="false">
                                <b-icon icon="three-dots-vertical"></b-icon>
                            </a>
                            <div class="dropdown-menu action-dropdown-menu" aria-labelledby="actionDropdown">
                                <b-button-group>
                                    <b-button variant="outline-secondary" @click="editData(item.id)" :disabled="item.is_gf == 1">
                                        <b-icon icon="pencil" class="mr-1"></b-icon> Edit
                                    </b-button>
                                    <b-button variant="outline-danger" @click="deleteData(item.id)" :disabled="item.is_gf == 1">
                                        <b-icon icon="trash" class="mr-1"></b-icon> Delete
                                    </b-button>
                                </b-button-group>
                            </div>
                        </td>
                    </tr>
                    
                    <!-- Pagination Footer Row -->
                    <tr v-if="paginatedData.length > 0">
                        <td :colspan="configMode == 2 ? 5 : 8" class="p-3 border-0">
                            <div class="row py-2 no-margin">
                                <div class="col-md-12 p-0 no-margin">
                                    <ul class="pagination b-pagination float-left mb-0 mt-1">
                                        <li class="page-item disabled m-2">View</li>
                                        <li class="page-item disabled">
                                            <b-form-select v-model="temp_length" :options="length_options"
                                                           @change="setFirstPage()"></b-form-select>
                                        </li>
                                        <li class="page-item disabled m-2">per page. Total {{ filteredData.length }}
                                            records found.
                                        </li>
                                    </ul>
                                    <b-pagination-nav class="float-right mt-1"
                                                      v-model="pagination.currentPage"
                                                      :number-of-pages="pagination.pages" prev-text="Previous"
                                                      next-text="Next" base-url="#" first-number
                                                      last-number></b-pagination-nav>
                                </div>
                            </div>
                        </td>
                    </tr>
                    
                    <tr v-if="paginatedData.length === 0">
                        <td :colspan="configMode == 2 ? 5 : 8" class="text-center p-4 text-secondary">{{ blankMsg }}</td>
                    </tr>
                </tbody>
            </table>
        </div>


        <b-modal id="bv-modal-template-preview" dialog-class="modal-xml" size="lg" hide-footer >
            <template #modal-title>
                Template Details
            </template>   
                               
                <b-form @submit.stop.prevent="saveTemplate" class="">
                    <input type="hidden" v-model="fdata.id" name="id">
                    <div class="clearfix">

                        <ckeditor @namespaceloaded="onNamespaceLoaded" :config="editorConfig" v-model="fdata.content"></ckeditor>
                        <div class="mt-3">
                            <b-button type="submit" variant="primary" style=""><b-icon variant="light" icon="clipboard-plus" scale="1" width="20px" height="20px" class="align-left mr-2"></b-icon>Save Template</b-button>
                        </div>                        
                    </div>
                </b-form>
            
        </b-modal>

        <b-modal id="bv-modal-cost-settings" size="md" hide-footer>
            <template #modal-title>
                <div v-if="!data.id">Create New CTC Item</div>
                <div v-else>Update CTC Item</div>
                <div class="modal-subtitle" v-if="!data.id">Enter the details of the new CTC item.</div>
                <div class="modal-subtitle" v-else>Update the details of the CTC item.</div>
            </template>
            <b-form @submit.stop.prevent="onSubmit">
                <input type="hidden" v-model="data.id" name="id">

                <div class="modal-form-card">
                    <b-form-group id="name-group" label="Item Name *" label-for="name">
                        <b-form-input id="name" name="name" v-model="data.name" placeholder="e.g., Basic Salary"
                                        v-validate="{ required: true, min: 3, regex: /^[A-Za-z0-9\s]+$/, max: 50 }"
                                        :state="validateState('name')" aria-describedby="name-feedback"
                                        data-vv-as="Item Name"></b-form-input>
                        <b-form-invalid-feedback id="name-feedback">{{ veeErrors.first('name') }}</b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group id="type-group" label="Type *" label-for="type">
                        <b-form-select id="type" name="type" v-model="data.type" :options="types"
                                       v-validate="{ required: true }" :state="validateState('type')" 
                                       aria-describedby="type-feedback" data-vv-as="Type"></b-form-select>
                        <b-form-invalid-feedback id="type-feedback">{{ veeErrors.first('type') }}</b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group v-if="configMode != 2" id="category-group" label="Category *" label-for="category">
                        <b-form-select id="category" name="category" v-model="data.category" :options="categories"
                                       v-validate="{ required: true }" :state="validateState('category')" 
                                       aria-describedby="category-feedback" data-vv-as="Category"></b-form-select>
                        <b-form-invalid-feedback id="category-feedback">{{ veeErrors.first('category') }}</b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group v-if="configMode != 2" label="Applicable Departments *">
                        <input
                            id="departments"
                            type="hidden"
                            name="departments"
                            v-model="data.departments"
                            v-validate="'required'"
                            data-vv-as="Applicable Departments"
                        >
                        <div
                            :class="[
                                selectedDepartmentTags.length ? 'dept-selector-wrapper' : '',
                                { 'border-danger': validateState('departments') === false }
                            ]"
                        >
                            <div v-if="selectedDepartmentTags.length" class="mb-2">
                                <b-badge
                                    v-for="dept in selectedDepartmentTags"
                                    :key="dept.value"
                                    variant="light"
                                    class="dept-tag mr-2 mb-2"
                                >
                                    {{ dept.text }}
                                    <span
                                        class="dept-tag-close"
                                        @click="removeDepartment(dept.value)"
                                    >
                                        &times;
                                    </span>
                                </b-badge>
                            </div>
                            <div v-if="isAllDepartmentSelected" class="text-muted small mb-2 font-weight-500">
                                Note: Individual department selection is disabled when "All Department" is selected.
                            </div>
                            <b-form-select
                                v-model="selectedDepartmentToAdd"
                                :options="availableDepartmentOptions"
                                @change="addDepartment"
                                :disabled="isAllDepartmentSelected"
                                class="dept-select-control"
                            >
                                <template #first>
                                    <b-form-select-option :value="null">Select Department</b-form-select-option>
                                </template>
                            </b-form-select>
                        </div>
                        <div class="text-danger small mt-1" v-if="validateState('departments') === false">{{ veeErrors.first('departments') }}</div>
                    </b-form-group>

                    <b-form-group id="frequency-group" label="Payment Frequency *" label-for="payment_frequency">
                        <b-form-select id="payment_frequency" name="payment_frequency" v-model="data.payment_frequency" :options="frequencies"
                                       v-validate="{ required: true }" :state="validateState('payment_frequency')" 
                                       aria-describedby="frequency-feedback" data-vv-as="Payment Frequency"></b-form-select>
                        <b-form-invalid-feedback id="frequency-feedback">{{ veeErrors.first('payment_frequency') }}</b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group v-if="configMode != 2" label="Payment Timing *">
                        <b-form-radio-group id="payment_timing" v-model="data.payment_timing" name="payment_timing" v-validate="{ required: true }" data-vv-as="Payment Timing">
                            <div class="mb-2" v-for="timing in timings" :key="timing.value">
                                <b-form-radio :value="timing.value">{{ timing.text }}</b-form-radio>
                            </div>
                        </b-form-radio-group>
                        <div class="text-danger small mt-1" v-if="validateState('payment_timing') === false">{{ veeErrors.first('payment_timing') }}</div>
                    </b-form-group>

                    <!-- Conditional rules replace the fixed amount rather than sit
                         beside it, so the field is taken off screen (and out of
                         validation and the payload) while the rule builder is open. -->
                    <b-form-group v-if="configMode != 2 && !showConditionalRules" id="amount-group" label="Amount (BDT) *" label-for="fixed_amount">
                        <!-- `no-spin` drops the stepper arrows and the wheel handler
                             stops a stray scroll over a focused field from silently
                             nudging the amount. -->
                        <b-form-input id="fixed_amount" name="fixed_amount" v-model="data.fixed_amount" type="number" step="any"
                                        min="0" class="no-spin" @wheel.native.prevent="$event.target.blur()"
                                        v-validate="{ required: true, min_value: 0, max_value: 9999999999999.99 }"
                                        :state="validateState('fixed_amount')" aria-describedby="amount-feedback"
                                        data-vv-as="Amount"></b-form-input>
                        <b-form-invalid-feedback id="amount-feedback">{{ veeErrors.first('fixed_amount') }}</b-form-invalid-feedback>
                    </b-form-group>

                    <div v-if="configMode != 2" class="mb-3">
                        <button
                            type="button"
                            class="btn btn-toggle-rules"
                            :class="showConditionalRules ? 'btn-cancel-cost' : 'btn-submit-cost'"
                            @click="toggleConditionalRules"
                        >
                            {{ showConditionalRules ? 'Hide' : 'Show' }} Conditional Amount Rules
                        </button>
                    </div>

                    <div v-if="showConditionalRules && configMode != 2" class="mb-3">
                        <div class="tree-builder-caption">Conditional Amount Rules (Tree Builder)</div>
                        <ctc-rule-builder v-model="data.rule_config" :fields="customAttributes" @reset="onRuleReset" />
                    </div>

                    <b-form-group id="status-group" label="Status *" label-for="status">
                        <b-form-select id="status" name="status" v-model="data.status" :options="status"
                                       v-validate="{ required: true }" :state="validateState('status')" 
                                       aria-describedby="status-feedback" data-vv-as="Status"></b-form-select>
                        <b-form-invalid-feedback id="status-feedback">{{ veeErrors.first('status') }}</b-form-invalid-feedback>
                    </b-form-group>
                </div>

                <div class="d-flex justify-content-center mt-4 pb-2">
                    <button type="button" class="btn btn-cancel-cost mr-3" @click="$bvModal.hide('bv-modal-cost-settings')">Cancel</button>
                    <button type="submit" class="btn btn-submit-cost">{{ data.id ? 'Update CTC Item' : 'Create CTC Item' }}</button>
                </div>
            </b-form>
        </b-modal>
    </div>
</template>
<script>
import ctcOverlay from '../js/ctcOverlay';
export default {
    mixins: [ctcOverlay],


    mounted() {
        // this.permission = this.$acl['leave-type'];
    },
    props: {
        ctcSettings: {
            type: Object,
            default: null
        }
    },
    watch: {
        ctcSettings: {
            immediate: true,
            handler(newSettings) {
                if (newSettings) {
                    this.applyCtcSettings(newSettings);
                }
            }
        },
        showOverlay(newVal) {
            this.ctcOverlay(newVal);
        }
    },
    data() {
        let sortOrders = {};
        let columns = [
            {label: 'Upload Data', name: 'name', sort: true},
            {label: 'Type', name: 'type', sort: true},
            {label: 'Category', name: 'category', sort: true},
            {label: 'Payment Frequency', name: 'payment_frequency', sort: true},
            {label: 'Amount', name: 'fixed_amount', sort: true},
            {label: 'Departments', name: 'departments', sort: false},
            {label: 'Status', name: 'status'},
        ];
        columns.forEach((column) => {
            sortOrders[column.name] = -1;
        });

        return {
            blankMsg: 'Fetching data..',

            datalist: [],
            showOverlay: false,
            configMode: 1,
            data: {name:'', status:'active', type:'compensation', category:'main_part', departments:[], payment_frequency:'monthly', payment_timing:'', fixed_amount:'', custom_attributes: {}, rule_config: { field: '', nodes: [] }},
            customAttributes: [],
            selectedDepartmentToAdd: null,
            showConditionalRules: false,

            search: '',
            columns: columns,
            sortKey: 'name',
            sortOrders: sortOrders,
            length: 10,
            temp_length: 10,
            length_options: [
                {value: 5, text: "5"},
                {value: 10, text: "10"},
                {value: 20, text: "20"},
                {value: 50, text: "50"}
            ],
            pagination: {currentPage: 1, total: '', nextPage: '', prevPage: '', from: '', to: '', pages: 1},

            status: [
                {value: "inactive", text: "Inactive"},
                {value: "active", text: "Active"},
            ],
            types: [
                {value: "compensation", text: "Compensation"},
                {value: "annual", text: "Annual"},
            ],
            categories: [
                {value: "main_part", text: "Main Part"},
                {value: "other_part", text: "Other Part"},
            ],
            frequencies: [
                {value: "yearly", text: "Yearly"},
                {value: "half_yearly", text: "Half-Yearly"},
                {value: "quarterly", text: "Quarterly"},
                {value: "monthly", text: "Monthly"},
            ],
            departments: [],

            cost:0,
            template_type : 1,

            editorConfig: {
                    extraPlugins: ['placeholder_select' , 'justify'],
                    //extraPlugins: 'strinsert',
                    placeholder_select:{
                        placeholders: [],
                        format:' [[%placeholder%]] ',
                    },
                    allowedContent : true,

                },
            fdata: { id:null , title:'' , category:0 , type:0 , content:'' , duplicate:0, signature:0},
            placeholder : [],
        }
    },
    methods: {
        serverError(error) {
            if (error.response && error.response.data) {
                const data = error.response.data;
                // A 422 carries the useful detail in `errors` (e.g. "The name has
                // already been taken."). Showing only `message` surfaced the
                // useless generic "The given data was invalid." instead.
                const fieldErrors = data.errors
                    ? Object.values(data.errors).reduce((all, msgs) => all.concat(msgs), [])
                    : [];

                const errorMessage = fieldErrors.length
                    ? fieldErrors.join(' ')
                    : (data.message || data.error || 'An unexpected error occurred.');

                this.$bvToast.toast(errorMessage, {
                    title: error.response.status === 422 ? 'Validation Error' : 'Server Error',
                    variant: 'danger',
                    solid: true
                });
            } else {
                console.error(error);
                this.$bvToast.toast('An unexpected error occurred.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            }
        },
        getTypeText(val) {
            let item = this.types.find(t => t.value === val);
            return item ? item.text : val;
        },
        getCategoryText(val) {
            let item = this.categories.find(c => c.value === val);
            return item ? item.text : val;
        },
        getFrequencyText(val) {
            let item = this.frequencies.find(f => f.value === val);
            return item ? item.text : val;
        },
        formatAmount(amount) {
            return Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
        },
        validateState(ref) {
            if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
                return !this.veeErrors.has(ref);
            }
            return null;
        },
        normalizeDepartmentValue(value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return null;
            }
            return String(value);
        },
        hasMeaningfulRuleConfig(ruleConfig) {
            if (!ruleConfig || typeof ruleConfig !== 'object') {
                return false;
            }

            const hasField = !!ruleConfig.field;
            const hasNodes = Array.isArray(ruleConfig.nodes) && ruleConfig.nodes.length > 0;

            return hasField || hasNodes;
        },
        resetForm() {
            this.data = {name:'', status:'active', type:'compensation', category:'main_part', departments:[], payment_frequency:'monthly', payment_timing:'', fixed_amount:'', custom_attributes: {}, rule_config: { field: '', nodes: [] }};
            this.selectedDepartmentToAdd = null;
            this.showConditionalRules = false;
            this.$nextTick(() => {
                this.$validator.reset();
            });
        },
        addDepartment(value) {
            const normalizedValue = this.normalizeDepartmentValue(value);

            if (!normalizedValue) {
                return;
            }

            if (normalizedValue === "-1") {
                this.data.departments = ["-1"];
            } else if (!this.data.departments.includes(normalizedValue)) {
                this.data.departments.push(normalizedValue);
            }

            this.selectedDepartmentToAdd = null;
            this.$nextTick(() => this.$validator.validate('departments', this.data.departments));
        },
        removeDepartment(departmentValue) {
            const normalizedValue = this.normalizeDepartmentValue(departmentValue);
            this.data.departments = this.data.departments.filter(dept => dept !== normalizedValue);
            this.$nextTick(() => this.$validator.validate('departments', this.data.departments));
        },
        onRuleReset() {
            this.data.custom_attributes = {};
        },
        toggleConditionalRules() {
            this.showConditionalRules = !this.showConditionalRules;
            if (!this.showConditionalRules) {
                this.data.custom_attributes = {};
                this.data.rule_config = { field: '', nodes: [] };
            }
        },
        addNew() {
            this.resetForm();
            this.$bvModal.show('bv-modal-cost-settings');
        },
        getData: function () {
            if (!this.checkNet()) return false;
            let currentObj = this;
            axios.get('/cost-to-company/heads').then(function (response) {
                if (response.data.data.length === 0)
                {
                    currentObj.blankMsg = "Nothing found.";
                }
                currentObj.datalist = response.data.data;
            }.bind(this)).catch(function (error) {
                currentObj.serverError(error);
            });
        },
        editData: function (x) {
            if (!this.checkNet()) return false;
            this.showOverlay = true;
            let currentObj = this;
            axios.get('/cost-to-company/heads/' + x).then(function (response) {
                let item = response.data.data;
                if (item.departments) {
                    item.departments = item.departments.map(d => this.normalizeDepartmentValue(d.id));
                } else {
                    item.departments = [];
                }
                if (!item.custom_attributes) {
                    item.custom_attributes = {};
                } else if (Array.isArray(item.custom_attributes)) {
                    // if it returned as an empty array [] instead of object {} from backend
                    item.custom_attributes = Object.assign({}, item.custom_attributes);
                }

                if (!item.rule_config) {
                    item.rule_config = { field: '', nodes: [] };
                } else if (typeof item.rule_config === 'string') {
                    try {
                        item.rule_config = JSON.parse(item.rule_config);
                    } catch (e) {
                        item.rule_config = { field: '', nodes: [] };
                    }
                }
                
                currentObj.customAttributes.forEach(attr => {
                    if (attr.type == 8) {
                        if (!item.custom_attributes[attr.id]) {
                            item.custom_attributes[attr.id] = [];
                        } else if (!Array.isArray(item.custom_attributes[attr.id])) {
                            item.custom_attributes[attr.id] = [item.custom_attributes[attr.id]];
                        }
                    } else if (attr.type == 7) {
                        if (Array.isArray(item.custom_attributes[attr.id]) && item.custom_attributes[attr.id].length > 0) {
                            item.custom_attributes[attr.id] = item.custom_attributes[attr.id][0];
                        }
                    }
                });

                currentObj.data = item;
                currentObj.selectedDepartmentToAdd = null;
                currentObj.showConditionalRules = currentObj.hasMeaningfulRuleConfig(item.rule_config);
                currentObj.showOverlay = false;
                currentObj.$bvModal.show('bv-modal-cost-settings');
            }.bind(this)).catch(function (error) {
                currentObj.showOverlay = false;
                currentObj.serverError(error);
            });
        },
        deleteData(id) {
            if (!this.checkNet()) return false;
            let currentObj = this;

            this.$bvModal.msgBoxConfirm('Please confirm that you want to delete this head.', {
                title: 'Please Confirm',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'primary',
                okTitle: 'Yes',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            }).then(value => {
                if (value) {
                    axios.delete('/cost-to-company/heads/' + id).then(function (response) {
                        this.getData();
                        this.showOverlay = false;

                    }.bind(this))
                        .catch(function (error) {

                            currentObj.showOverlay = false;
                            currentObj.serverError(error);
                        });
                }
            });
        },
        onSubmit(e) {
            if (!this.checkNet()) return false;
            if (this.data.name) {
                this.data.name = this.data.name.replace(/\s+/g, ' ').trim();
            }
            this.$validator.validateAll().then(result => {
                if (!result) {
                    return;
                }

                // The rule builder is open but no attribute was picked, so nothing
                // would be saved and the hidden fixed amount would silently drive the
                // head again. Say so rather than let the server reject it.
                if (this.configMode != 2 && this.showConditionalRules && !(this.data.rule_config && this.data.rule_config.field)) {
                    this.$bvToast.toast('Select an attribute in the CTC Amount Rule Builder, or turn conditional rules off.', {
                        title: 'Validation Error',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }

                // Conditional rules supply the amount per employee — the fixed amount
                // is left out of the payload entirely so it can have no effect.
                const rulesActive = this.configMode != 2
                    && this.showConditionalRules
                    && this.hasMeaningfulRuleConfig(this.data.rule_config);

                this.showOverlay = true;

                e.preventDefault();
                let currentObj = this;

                let formData = new FormData();

                for (let [key, value] of Object.entries(this.data)) {
                    if (key === 'fixed_amount' && rulesActive) {
                        continue;
                    }
                    if (key === 'custom_attributes') {
                        if (this.showConditionalRules) continue;
                        for (let attrId in value) {
                            let optVal = value[attrId];
                            if (Array.isArray(optVal)) {
                                optVal.forEach(val => formData.append(`custom_attributes[${attrId}][]`, val));
                            } else if (optVal !== null && optVal !== 'null' && optVal !== '') {
                                formData.append(`custom_attributes[${attrId}]`, optVal);
                            }
                        }
                        continue;
                    }
                    if (key === 'rule_config') {
                        if (this.showConditionalRules && this.hasMeaningfulRuleConfig(value)) {
                            formData.append('rule_config', JSON.stringify(value));
                        }
                        continue;
                    }
                    if (value === null || value === 'null') value = '';
                    if (Array.isArray(value)) {
                        value.forEach(val => formData.append(`${key}[]`, val));
                    } else {
                        formData.append(key, value);
                    }
                }

                

                let url = '/cost-to-company/heads';

                if (this.data.id > 0) {
                    url = url + "/" + this.data.id + '?_method=PUT';
                }

                axios.post(url, formData)
                    .then(function (response) {
                        this.getData();
                        this.$bvToast.toast('Saved!', {
                            title: `Success`,
                            variant: 'success',
                            solid: true
                        });
                        this.showOverlay = false;
                        this.resetForm();
                        this.$bvModal.hide('bv-modal-cost-settings');
                    }.bind(this))
                    .catch(function (error) {
                        currentObj.showOverlay = false;
                        currentObj.serverError(error);
                    });
            });
        },
        onSubmitOverheadCost(e) {
            if (!this.checkNet()) return false;
            this.$validator.validateAll().then(result => {
                if (!result) {
                    return;
                }
                
                e.preventDefault();
                let currentObj = this;

                let formData = new FormData();

                formData.append('overheadcost', this.cost);

                let url = 'ctc/overhead';
                
                axios.post(url, formData)
                    .then(function (response) {
                        this.getData();
                        this.$bvToast.toast('Saved!', {
                            title: `Success`,
                            variant: 'success',
                            solid: true
                        });
                        
                    }.bind(this))
                    .catch(function (error) {
                        currentObj.showOverlay = false;
                        currentObj.serverError(error);
                    });
            });
        },
        openTemplate(type)
        {
            this.template_type = type;
            this.editorConfig.placeholder_select.placeholders = this.placeholder[8][1];

            if(!this.checkNet()) return false;
            let currentObj = this;
            axios.get(this.$api_url + '/cost-to-company/template?type='+type).then(function(response)
            {
                this.fdata = response.data;
            }.bind(this)).catch(function (error){ currentObj.serverError(error);});


            this.$bvModal.show('bv-modal-template-preview');    
        },
        saveTemplate()
        {
            let formData = new FormData();

            formData.append('fdata', this.fdata);

            for (let [key, value] of Object.entries(this.fdata)) 
            {                    
                var nm = `${key}`;
                var fval = `${value}`;
                formData.append(nm, fval);
            } 

            axios.post(this.$api_url+'/cost-to-company/template?type='+this.template_type, formData)
                        .then(function (response)
                        {                           

                            this.$bvToast.toast('Saved!', {
                                    title: `Success`,
                                    variant: 'success',
                                    solid: true
                                });

                            
                            this.$bvModal.hide('bv-modal-template-preview');

                        }.bind(this))
                        .catch(function (error)
                        {
                            currentObj.serverError(error);                            
                        });

        },
        getTemplatePlaceholderData()
        {
            if(!this.checkNet()) return false;
            let currentObj = this;
            axios.get(this.$api_url + '/cost-to-company/templates/placeholder' , {
                params: {
                    
                }
            }).then(function(response)
            {
                this.placeholder = response.data;
            }.bind(this)).catch(function (error){ currentObj.serverError(error);});
        },
        getCtcSettings() {
            if (this.ctcSettings) {
                this.applyCtcSettings(this.ctcSettings);
            }
        },
        applyCtcSettings(settings) {
            this.configMode = Number(settings.mode) || 1;
        },
        getActiveDepartments() {
            if(!this.checkNet()) return false;
            let currentObj = this;
            axios.get('/cost-to-company/active-departments').then(function(response) {
                const allDept = { value: "-1", text: "All Department" };
                currentObj.departments = [allDept, ...response.data.map(dept => ({
                    value: currentObj.normalizeDepartmentValue(dept.value),
                    text: dept.text
                }))];
            }).catch(function (error) { 
                currentObj.serverError(error); 
            });
        },
        getCustomAttributes() {
            if (!this.checkNet()) return false;
            let currentObj = this;
            axios.get('/cost-to-company/custom-attributes').then(function (response) {
                currentObj.customAttributes = response.data.data.map(attr => {
                    return { 
                        id: attr.id, 
                        label: attr.label, 
                        type: attr.type,
                        options: attr.options ? attr.options.map(o => ({value: o.id, text: o.label})) : [] 
                    };
                });
            }).catch(function (error) {
                currentObj.serverError(error);
            });
        },

        onNamespaceLoaded( CKEDITOR ) {
            CKEDITOR.plugins.addExternal('placeholder_select', this.$api_url+'/plugin/placeholder_select/','plugin.js');

        },

        
    },
    created: function () {
        this.getData();
        this.getTemplatePlaceholderData();
        this.getActiveDepartments();
        this.getCustomAttributes();
        this.getCtcSettings();
    },
    computed:
        {
            isAllDepartmentSelected() {
                return this.data.departments.some(d => String(d) === "-1");
            },
            filteredData() {
                let filtered_list = this.datalist;
                if (this.search) {
                    filtered_list = filtered_list.filter((row) => {
                        return Object.keys(row).some((key) => {
                            return String(row[key]).toLowerCase().indexOf(this.search.toLowerCase()) > -1;
                        })
                    });

                    if (filtered_list.length === 0)
                        this.blankmsg = "Nothing found.";
                }

                let sortKey = this.sortKey;
                let order = this.sortOrders[sortKey] || 1;
                if (sortKey) {
                    filtered_list = filtered_list.slice().sort((a, b) => {
                            let index = this.getIndex(this.columns, 'name', sortKey);
                            a = String(a[sortKey]).toLowerCase();
                            b = String(b[sortKey]).toLowerCase();
                            if (this.columns[index].type && this.columns[index].type === 'date') {
                                return (a === b ? 0 : new Date(a).getTime() > new Date(b).getTime() ? 1 : -1) * order;
                            } else if (this.columns[index].type && this.columns[index].type === 'number') {
                                return (+a === +b ? 0 : +a > +b ? 1 : -1) * order;
                            } else {
                                return (a === b ? 0 : a > b ? 1 : -1) * order;
                            }
                        }
                    );
                }

                return filtered_list;
            },
            paginatedData() {
                return this.paginate(this.filteredData, this.length, this.pagination.currentPage);
            },
            selectedDepartmentTags() {
                return this.data.departments
                    .map(selectedId => this.departments.find(dept => dept.value === this.normalizeDepartmentValue(selectedId)))
                    .filter(Boolean);
            },
            availableDepartmentOptions() {
                return this.departments.filter(dept => !this.data.departments.includes(dept.value));
            },
            timings() {
                const freq = this.data.payment_frequency;
                if (freq === 'yearly') {
                    return [
                        {value: "month", text: "Based On Current Year"},
                        {value: "date", text: "Based On Joining Date"},
                        {value: "next_month", text: "Effective From Next Year"},
                    ];
                } else if (freq === 'half_yearly') {
                    return [
                        {value: "month", text: "Based On Current Half-Year"},
                        {value: "date", text: "Based On Joining Date"},
                        {value: "next_month", text: "Effective From Next Half-Year"},
                    ];
                } else if (freq === 'quarterly') {
                    return [
                        {value: "month", text: "Based On Current Quarter"},
                        {value: "date", text: "Based On Joining Date"},
                        {value: "next_month", text: "Effective From Next Quarter"},
                    ];
                } else {
                    return [
                        {value: "month", text: "Based On Current Month"},
                        {value: "date", text: "Based On Joining Date"},
                        {value: "next_month", text: "Effective From Next Month"},
                    ];
                }
            },
        }
}
</script>

<style scoped>
/* Page header (.ctc-dashboard-title / .ctc-dashboard-subtitle) is styled
   centrally in resources/sass/_ctc.scss. */

/* The search box (.search-input-group) is styled centrally in
   resources/sass/_ctc.scss so every CTC list screen shares one definition. */

/* Colour comes from the global .btn-secondary rules; only geometry is set here.
   The plus icon in "Add New Cost" grows that button's line box, so the three
   actions would otherwise land on three different heights. Pin them to the 42px
   the search box uses so the whole row sits on one baseline. */
.ctc-header-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    padding: 0 20px !important;
    line-height: 1;
    white-space: nowrap;
}

/* The shared wrapper draws a 1px #e2e8f0 outline plus a drop shadow on all four
   sides. On this screen that leaves a pale line hugging the dark header and
   running the full height of the table on both edges — the design has no such
   outline, so drop the vertical ones (and the shadow that bleeds past them)
   while keeping the rounded top corners the header still clips against. */
.table-container-custom {
    border-left: none;
    border-right: none;
    box-shadow: none;
}

.ctc-config-table {
    width: 100%;
    border-collapse: collapse;
}

.ctc-config-table thead tr {
    background-color: #01524B;
}

/* The sort indicator is styled in resources/sass/_ctc.scss, in one block shared
   with the three <common-list> screens (Employees, Annual Reports, Audit Logs)
   so all four headers draw the same arrow pair. Only sortable columns carry a
   `sorting*` class, so Status and Departments stay bare. */

/* Record counter beside the search box — the employee list's blue pill
   (.employee-count-icon / .employee-count-number in custom.scss), restated here
   because that pair is absolutely positioned against the DemoList toolbar and
   would not sit in this row. Held to the 42px the search box uses. */
.ctc-count-badge {
    display: inline-flex;
    align-items: stretch;
    height: 42px;
    border: 1px solid #B5CBE2;
    border-radius: 5px;
    overflow: hidden;
    flex: 0 0 auto;
}

.ctc-count-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    background-color: #ffffff;
    border-right: 1px solid #B5CBE2;
    color: #0060C9;
}

.ctc-count-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 51px;
    padding: 0 10px;
    background-color: #E1F2FF;
    color: #0060C9;
    font-size: 15px;
    font-weight: 600;
}

/* Header bar and body cells mirror the Annual CTC Reports breakdown table: a
   45px dark header with no rules, then every body cell outlined in #E8E8E8 and
   zebra-striped, so the two CTC screens read as one design. */
.ctc-config-table th {
    color: #ffffff;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 14px;
    letter-spacing: -0.02em;
    height: 45px;
    padding: 10px;
    border: none !important;
    vertical-align: middle;
}

.ctc-config-table tbody tr.table-row td {
    height: 40px;
    padding: 10px;
    font-family: 'Poppins', sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: #34444C;
    vertical-align: middle;
    border: 1px solid #E8E8E8 !important;
}

/* Zebra striping, per the design */
.ctc-config-table tbody tr.table-row:nth-child(even) td {
    background-color: #F8F8F8;
}

/* The pagination and empty-state rows are not part of the grid. */
.ctc-config-table tbody tr:not(.table-row) td {
    border: none !important;
}

.ctc-config-table tbody tr.table-row:hover td {
    background-color: #f6f6f6 !important;
}

.font-weight-500 {
    font-weight: 500;
}

/* Badges */
.dept-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    background-color: #e0f2fe;
    color: #0284c7;
    border: 1px solid #bae6fd;
    white-space: nowrap;
}

/* Status pill — Figma "CTC Heads" status cell: 6px/10px padding, 6px gap,
   4px radius, 15px icon. The icon keeps its own colour (the positive green /
   neutral grey) while the label stays on the muted text tone, so the two do
   not share `currentColor`. */
.status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    /* "Active" is the shorter of the two labels, so the pill is held to the
       width the longer one ("Inactive") needs and its content centred. That
       keeps the two states the same size down the column. */
    min-width: 100px;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: -0.02em;
    white-space: nowrap;
}

.status-badge svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
}

.status-active {
    background-color: #F2FAF5;
    color: #46524D;
    border: 1px solid #BDDBCE;
}

.status-active svg {
    color: #069651;
}

.status-disabled {
    background-color: #FFFFFF;
    color: #747474;
    border: 1px solid #747474;
}

.status-disabled svg {
    color: #747474;
}

/* Actions dropdown — colour is left to the global .action-dropdown-toggle rule
   in custom.scss so the three-dots match every other list in the app. */
.action-dropdown-toggle {
    cursor: pointer;
    transition: color 0.2s;
}

.action-dropdown-menu.show {
    padding-left: 0;
    padding-right: 0;
}

.action-dropdown-menu {
    overflow: hidden;
}

/* Pagination Customization */
.page-size-select {
    border: 1px solid #E8E8E8;
    border-radius: 6px;
    color: #4b5563;
    background-color: #ffffff;
    font-size: 13px;
    padding: 2px 8px;
    outline: none;
    transition: border-color 0.2s;
}

.page-size-select:focus {
    border-color: #01524B;
    box-shadow: none;
}

/* Pagination is styled centrally in resources/sass/_ctc.scss. */

/* Parent modal overrides */
::v-deep .modal-content {
    border-radius: 16px !important;
    border: none !important;
    /* The dialog lifts off the backdrop rather than sitting flat on it, so the
       rounded edge reads as the form's own border. */
    box-shadow: 0 12px 32px rgba(1, 82, 75, 0.18) !important;
    background-color: #ffffff !important;
}
::v-deep .modal-header {
    display: block !important;
    text-align: center !important;
    position: relative !important;
    border-bottom: none !important;
    padding: 2rem 1.5rem 0.5rem 1.5rem !important;
    background-color: #ffffff !important;
    /* The header is opaque and .modal-content does not clip its children, so it
       has to carry the shell's own corner or it paints Bootstrap's ~4px radius
       over the rounded top — which is what squared off the delete dialog while
       its transparent footer stayed round. `inherit` picks up .modal-content's
       16px, so the two stay in step if that value ever changes. */
    border-top-left-radius: inherit !important;
    border-top-right-radius: inherit !important;
}
::v-deep .modal-title {
    font-size: 1.25rem !important;
    font-weight: 700 !important;
    color: #212529 !important;
    width: 100%;
}
/* Sits in the header's own top-right corner instead of level with the title —
   at 1.8rem it was as heavy as the heading and pushed against the rounded edge. */
::v-deep .modal-header .close {
    position: absolute !important;
    right: 1rem !important;
    top: 0.75rem !important;
    margin: 0 !important;
    padding: 0 !important;
    font-size: 1.15rem !important;
    line-height: 1 !important;
    color: #333 !important;
    opacity: 0.5;
    outline: none !important;
}
::v-deep .modal-header .close:hover {
    opacity: 0.8;
}
::v-deep .modal-body {
    padding: 1.5rem !important;
    background-color: #ffffff !important;
    /* Same reason as the header, for the footerless modals where the body is the
       last thing in the shell. Behind a footer the curve falls on matching
       white, so it costs nothing there. */
    border-bottom-left-radius: inherit !important;
    border-bottom-right-radius: inherit !important;
}

/* Modal subtitle */
.modal-subtitle {
    font-size: 14px;
    font-weight: normal;
    color: #727272;
    margin-top: 4px;
}

/* White card for form inside modal */
.modal-form-card {
    background-color: #ffffff;
    border: 1px solid #E8E8E8;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.02);
    margin-bottom: 20px;
}

/* Form styling inside modal. Everything below is prefixed with `.modal-form-card`
   so this !important styling reaches only the modal's own fields — the pagination
   footer's per-page selector and the page search box are system chrome and keep
   their shared look. */
/* `b-form-group` renders a <legend> instead of a <label> when no `label-for` is
   given (Applicable Departments, Payment Timing — both wrap groups of controls,
   so a legend is correct). Style both or those two inherit Bootstrap's default
   legend colour and 1.5rem size and read as a different field. */
.modal-form-card ::v-deep .form-group label,
.modal-form-card ::v-deep .form-group legend {
    font-weight: 600 !important;
    color: #01524B !important;
    font-size: 13px !important;
    margin-bottom: 8px !important;
}

/* Number fields marked `no-spin` lose the browser stepper arrows: the amount is
   typed, and the arrows only invite an accidental click that changes it. The
   design closes the field with the stepper's up/down pair; only the lower angle
   is kept, so it matches the single chevron every select on this form ends in. */
.modal-form-card ::v-deep .no-spin::-webkit-outer-spin-button,
.modal-form-card ::v-deep .no-spin::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
}

.modal-form-card ::v-deep .no-spin {
    -moz-appearance: textfield !important;
    appearance: textfield !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%2301524B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='M4 6l4 4 4-4'/%3e%3c/svg%3e") !important;
    background-repeat: no-repeat !important;
    background-position: right 12px center !important;
    background-size: 14px 14px !important;
    padding-right: 34px !important;
}

/* Unscoped, this border also landed on the search input — inside the one
   `.search-input-group` already paints — so the box read as two nested boxes. */
.modal-form-card ::v-deep .form-control,
.modal-form-card ::v-deep .custom-select {
    border: 1.5px solid #01524B !important;
    border-radius: 8px !important;
    height: 42px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #01524B !important;
    background-color: #ffffff !important;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.modal-form-card ::v-deep .form-control::placeholder {
    color: #94a3b8 !important;
    font-weight: 400 !important;
}

.modal-form-card ::v-deep .form-control:focus,
.modal-form-card ::v-deep .custom-select:focus {
    border-color: #01524B !important;
    box-shadow: 0 0 0 3px rgba(4, 84, 76, 0.15) !important;
}

.modal-form-card ::v-deep .form-control:disabled,
.modal-form-card ::v-deep .custom-select:disabled {
    background-color: #f1f5f9 !important;
    border-color: #cbd5e1 !important;
    color: #94a3b8 !important;
    cursor: not-allowed;
}

/* A single teal chevron rather than Bootstrap's stacked up/down pair — the pair
   reads as a stepper the user can click to change the value, which a select isn't. */
.modal-form-card ::v-deep .custom-select {
    appearance: none !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%2301524B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='M4 6l4 4 4-4'/%3e%3c/svg%3e") !important;
    background-repeat: no-repeat !important;
    background-position: right 12px center !important;
    background-size: 14px 14px !important;
    padding-right: 34px !important;
}

/* Department selector wrapper when tags exist */
.dept-selector-wrapper {
    border: 1.5px solid #01524B;
    border-radius: 8px;
    padding: 12px;
    background-color: #ffffff;
}

/* Figma "Create New CTC Item form" department tag: a white pill outlined in
   #01524B at 4px radius, 5px/10px padding and a 10px gap, with the label in
   Poppins 500 15px on the primary #004242. Height comes out at 33px from
   21px line-height + 5px padding + 1px border top and bottom — left as
   line-height rather than a fixed height so long names can't clip. Width
   hugs the label. `variant="light"` on the b-badge is what needs overriding
   here: Bootstrap paints it #f8f9fa. */
.dept-tag {
    background-color: #ffffff !important;
    color: #004242 !important;
    border: 1px solid #01524B !important;
    padding: 5px 10px !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    line-height: 21px !important;
    letter-spacing: -0.02em;
    border-radius: 4px !important;
    display: inline-flex;
    align-items: center;
    gap: 10px;
}

/* The design's 10px × mark, in its own coral — it is the one destructive
   control on the pill, so it does not share the label's teal. The glyph's ink
   is about half its font-size, hence 20px for a 10px cross. */
.dept-tag-close {
    color: #F05B64;
    font-size: 20px;
    font-weight: 500;
    cursor: pointer;
    line-height: 1;
}

.dept-tag-close:hover {
    color: #d94550;
}

/* Radio buttons customization */
::v-deep .custom-radio .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #0284c7 !important;
    border-color: #0284c7 !important;
}

::v-deep .custom-radio .custom-control-label::before {
    border: 2px solid #cbd5e1 !important;
    width: 20px !important;
    height: 20px !important;
    top: 2px !important;
    border-radius: 50% !important;
}

::v-deep .custom-radio .custom-control-label::after {
    width: 20px !important;
    height: 20px !important;
    top: 2px !important;
    border-radius: 50% !important;
}

::v-deep .custom-radio .custom-control-label {
    padding-left: 8px !important;
    font-size: 15px !important;
    color: #4b5563 !important;
    line-height: 24px !important;
}

/* Names the block below the toggle, per the design. */
.tree-builder-caption {
    font-size: 14px;
    font-weight: 500;
    line-height: 23px;
    letter-spacing: -0.02em;
    color: #004242;
    margin-bottom: 8px;
}

/* Both footer buttons share height, padding and min-width so the pair reads as
   one control group — "Cancel" alone would otherwise come out visibly narrower
   than "Create CTC Item". */
.btn-cancel-cost,
.btn-submit-cost {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    min-width: 150px;
    padding: 0 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
}

/* Same palette as "Add New Cost" in the panel header — the global .btn-secondary
   colours ($admin-primary-btn #004242, hover to the pale #DDF5F9 fill with the
   #0892A4 edge), restated here because this is a raw <button>, not a b-button,
   so it never picks the system rules up. */
.btn-submit-cost {
    background-color: #004242 !important;
    border-color: #004242 !important;
    color: #ffffff !important;
    transition: all 0.2s;
}

.btn-submit-cost:hover {
    background-color: #DDF5F9 !important;
    border-color: #0892A4 !important;
    color: #004242 !important;
}

/* The exact mirror of the submit button, and the same treatment "Upload Payment
   Data" wears on the Annual CTC Reports panel (.custom-wizard-btn in
   resources/sass/atlhr.scss): the pale #DDF5F9 fill with the #0892A4 edge,
   turning solid #004242 on hover. The old off-palette #cbeee9 pair belonged to
   no system button. */
.btn-cancel-cost {
    color: #004242 !important;
    border: 1.5px solid #0892A4 !important;
    background-color: #DDF5F9 !important;
    transition: all 0.2s;
}

.btn-cancel-cost:hover {
    background-color: #004242 !important;
    border-color: #004242 !important;
    color: #ffffff !important;
}

/* Toggle rules button. Colour and hover come entirely from the modal footer
   pair: solid #004242 (.btn-submit-cost, as on "Create CTC Item") while the
   rules are hidden, flipping to the pale outlined .btn-cancel-cost once they
   are on screen — the two design frames show it that way, and sharing the
   classes keeps the three buttons on one palette. Only geometry is set here,
   and it is stated after those rules so this narrower, 39px button keeps its
   own size rather than the footer's 44px / 150px block. */
.btn-toggle-rules {
    font-size: 15px;
    letter-spacing: -0.04em;
    min-width: 0;
    padding: 10px 30px;
    height: 39px;
    line-height: 19px;
}
</style>
